import validator from 'validator';
import * as z from 'zod';

import { InvoiceStatus, LineupStatus, MarketReportInterval, TeamRole, UserRole } from '@/prisma/types';

// import { UserRole } from "@/prisma/client";

// Regular expressions for character checks
const uppercaseRegex = /[A-Z]/;
const lowercaseRegex = /[a-z]/;
const digitRegex = /[0-9]/;
const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

const passwordSchema = z
    .string()
    .min(8, { message: 'Password must be at least 8 characters long.' })
    .max(70, { message: "Password can't be longer then 70 characters long." })
    .refine((value) => uppercaseRegex.test(value), {
        message: 'Password must contain at least one uppercase letter.',
    })
    .refine((value) => lowercaseRegex.test(value), {
        message: 'Password must contain at least one lowercase letter.',
    })
    .refine((value) => digitRegex.test(value), {
        message: 'Password must contain at least one digit.',
    })
    .refine((value) => specialCharRegex.test(value), {
        message: 'Password must contain at least one special character.',
    });

export const SettingsSchema = z
    .object({
        name: z.optional(z.string()),
        role: z.enum([UserRole.ADMIN, UserRole.USER, UserRole.BROKER, UserRole.SALESPERSON]),
        email: z.optional(z.string().email()),
        password: z.optional(passwordSchema),
        newPassword: z.optional(passwordSchema),
    })
    .refine(
        (data) => {
            if (data.password && !data.newPassword) {
                return false;
            }

            return true;
        },
        {
            message: 'New password is required!',
            path: ['newPassword'],
        }
    )
    .refine(
        (data) => {
            if (data.newPassword && !data.password) {
                return false;
            }

            return true;
        },
        {
            message: 'Password is required!',
            path: ['password'],
        }
    );

export const NewPasswordSchema = z.object({
    password: passwordSchema,
    confirmPassword: passwordSchema,
});

export const ResetSchema = z.object({
    email: z.string().email({
        message: 'Email is required',
    }),
});

export const FreeEmailReportSignupSchema = z.object({
    email: z.string().email({
        message: 'Email is required',
    }),
});

export const LoginSchema = z.object({
    email: z.string().email({
        message: 'Email is required',
    }),
    password: z.string().min(1, {
        message: 'Password is required',
    }),
    next: z.string().optional(),
});

const otpCodeSchema = z.preprocess(
    (value) => {
        if (typeof value !== 'string') {
            return value;
        }

        return value.trim();
    },
    z.string().regex(/^\d{6}$/, {
        message: 'Code must contain 6 digits',
    })
);

export const LoginOtpRequestSchema = z.object({
    email: z.string().email({
        message: 'Email is required',
    }),
    next: z.string().optional(),
});

export const LoginOtpVerifySchema = LoginOtpRequestSchema.extend({
    code: otpCodeSchema,
    challengeId: z.string().uuid({
        message: 'Challenge is invalid',
    }),
});

export const EmailOnlySchema = z.object({
    email: z.string().email({
        message: 'Email is required',
    }),
    apikey: z.string(),
});

// Register Schema
export const RegisterSchema = z.object({
    firstName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    lastName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    email: z
        .string()
        .email({
            message: 'Invalid email address.',
        })
        .max(100, {
            message: 'Must be at most 100 characters.',
        }),
    password: passwordSchema,
    next: z.string().optional(),
});

export const JoinTeamSchema = z.object({
    firstName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    lastName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    password: passwordSchema,
});

export const CreateAccountSchema = z.object({
    firstName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    lastName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    password: passwordSchema,
});

// Permission Template
export const NewPermissionSchema = z.object({
    name: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    pageId: z.string().length(32, {
        message: 'UUID - Must be 32 characters long.',
    }),
    type: z.enum(['Module', 'Page', 'Entity']),
});

// Permission Instance
export const NewPermissionInstanceSchema = z.object({
    note: z
        .string()
        .max(400, {
            message: 'Must be at most 400 characters.',
        })
        .optional(),
    startDate: z.date(),
    endDate: z.date().optional(),
    level: z.enum(['VIEW', 'WRITE', 'FULL']),
    status: z.enum(['ACTIVE', 'INACTIVE']),
    permissionTemplateId: z.coerce.number().int(),
    userId: z.string(),
});

// Subscription Template
export const NewSubscriptionTemplateSchema = z.object({
    name: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    note: z
        .string()
        .max(400, {
            message: 'Must be at most 400 characters.',
        })
        .optional(),
    permissions: z.array(z.number()),
});

// Default Subscription Pricing
export const AddDefaultPricingSchema = z.object({
    subscriptionTemplateId: z.coerce.number().int(),
    userPrice: z.coerce.number(),
    basePrice: z.coerce.number(),
    duration: z.coerce.number().int().gte(1),
});

// Team
export const NewTeamSchema = z.object({
    name: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    companyName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    subscriptionDuration: z.coerce.number().int().gte(1),
});

export const TeamSubscriptionPriceModelSchema = z.object({
    id: z.coerce.number(),
    userPrice: z.coerce.number(),
    basePrice: z.coerce.number(),
    subscriptionTemplateId: z.coerce.number(),
});

export const TeamSubscriptionPriceModelArraySchema = z.array(TeamSubscriptionPriceModelSchema);

export const NewTeamWithSubscriptionSchema = z.object({
    name: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    subscriptionDuration: z.coerce.number().int().gte(1),
    subscriptionPrices: TeamSubscriptionPriceModelArraySchema,
});

export const AddUserToSubscriptionSchema = z.object({
    subscriptionInstanceId: z.coerce.number().int(),
});

export const AddSalesResponsibleToClientSchema = z.object({
    userId: z.string(),
    salesPersonId: z.string().optional(),
});

export const UpdateSubscriptionStatusSchema = z.object({
    subscriptionId: z.coerce.number().int(),
    status: z.enum(['ACTIVE', 'INACTIVE', 'EXPIRED', 'UPCOMING', 'CANCELLED']).optional(),
});

export const UsersToTeamSchema = z.object({
    teamId: z.coerce.number().int(),
    userIds: z.array(z.string()),
});

export const SubscriptionToTeamSchema = z.object({
    teamId: z.coerce.number().int(),
    subscriptionTemplateId: z.coerce.number().int(),
    startDate: z.date(),
    endDate: z.date(),
    totalPrice: z.coerce.number(),
    status: z.enum(['ACTIVE', 'INACTIVE', 'EXPIRED']),
});

export const RemoveUserFromSubscriptionSchema = z.object({
    subscriptionInstanceId: z.coerce.number().int(),
});

export const AddTeamSubscriptionPriceSchema = z.object({
    teamId: z.coerce.number().int(),
    subscriptionTemplateId: z.coerce.number(),
    price: z.coerce.number(),
});

export const UserDetailsSchema = z.object({
    userId: z.string(),
    phone: z
        .string()
        .max(20, {
            message: 'Must be at most 20 characters.',
        })
        .min(1, {
            message: 'Please provide a phone number.',
        })
        .min(6, {
            message: 'Must be at least 6 characters.',
        })
        .refine(validator.isMobilePhone, {
            message: 'Please provide a valid phone number.',
        }),
    company: z
        .string()
        .max(50, {
            message: 'Must be at most 50 characters.',
        })
        .min(4, {
            message: 'Must be at least 4 characters.',
        }),
    countryName: z.string().min(1, {
        message: 'Please provide a country.',
    }),
});

// Estimated Revenue
export const UpdateEstimatedRevenueSchema = z.object({
    userId: z.string(),
    estimatedRevenue: z.coerce.number().int(),
});

// Comment Instance
export const AddCommentSchema = z.object({
    body: z
        .string()
        .min(1, {
            message: 'Must be at least 1 characters.',
        })
        .max(4000, {
            message: 'Must be at most 4000 characters.',
        }),
});

// Market Report Template
export const MarketReportTemplateSchema = z.object({
    name: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(100, {
            message: 'Must be at most 100 characters.',
        }),
    displayName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(100, {
            message: 'Must be at most 100 characters.',
        }),
    description: z
        .string()
        .max(400, {
            message: 'Must be at most 400 characters.',
        })
        .optional()
        .nullable(),
    legacyListId: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(100, {
            message: 'Must be at most 100 characters.',
        }),
    interval: z.nativeEnum(MarketReportInterval),
});

export const MarketReportTemplateSchemaEdit = MarketReportTemplateSchema.extend({
    priority: z.coerce.number().int(),
    subscriptionTemplateIds: z.array(z.coerce.number()),
});

// Market Report Template Email Generation History
export const MarketReportTemplateHistorySchema = z.object({
    marketReportTemplateId: z.coerce.number().int(),
    addSectionToBox: z.boolean().default(false),
    additionalInformation: z.any().optional().default({}),
    senderEmail: z.string().email({ message: 'Must be a valid email address.' }),
    group: z.string().optional(),
    report: z.string().optional(),
    logo: z.string().min(1, { message: 'Logo is required.' }),
    subject: z.string().min(1, { message: 'Subject is required.' }),
    content: z.string().optional(),
    cropTop: z.coerce.number(),
    cropBottom: z.coerce.number(),
    footer: z.string().min(1, { message: 'Footer is required.' }),
});

const optionSchema = z.object({
    label: z.string(),
    value: z.string(),
    disable: z.boolean().optional(),
});

export const NewNewsSchema = z.object({
    createdByUserId: z.string(),
    dateOfReport: z.union([z.date(), z.null()]).optional(),
    body: z
        .string()
        .min(1, {
            message: 'Must be at least 1 characters.',
        })
        .max(4000, {
            message: 'Must be at most 4000 characters.',
        }),
    relatedProductOptions: z.optional(z.array(optionSchema)),
    relatedCountryOptions: z.optional(z.array(optionSchema)),
    relatedRegionOptions: z.optional(z.array(optionSchema)),
    relatedCategoryOptions: z.optional(z.array(optionSchema)),
});

export const NewMarketEventSchema = z.object({
    createdByUserId: z.string(),
    publishedAt: z.date(),
    timeOfEvent: z.date(),
    subject: z.string().max(200, {
        message: 'Must be at most 100 characters.',
    }),
    body: z
        .string()
        .min(1, {
            message: 'Must be at least 1 characters.',
        })
        .max(4000, {
            message: 'Must be at most 4000 characters.',
        }),
    relatedProductOptions: z.optional(z.array(optionSchema)),
    relatedCountryOptions: z.optional(z.array(optionSchema)),
    visibility: z.enum(['EXTERNAL', 'INTERNAL']),
});

export const NewMarketCommentarySchema = z.object({
    createdByUserId: z.string(),
    dateOfReport: z.union([z.date(), z.null()]).optional(),
    body: z
        .string()
        .min(1, {
            message: 'Must be at least 1 characters.',
        })
        .max(4000, {
            message: 'Must be at most 4000 characters.',
        }),
    relatedProductOptions: z.optional(z.array(optionSchema)),
    relatedCountryOptions: z.optional(z.array(optionSchema)),
    relatedRegionOptions: z.optional(z.array(optionSchema)),
});

const addInvoiceInformationString = () =>
    z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        });

const addInvoiceInformationStringOptional = () => z.union([addInvoiceInformationString(), z.literal('')]);

const AddInvoiceInformationPersonalSchema = z.object({
    accountType: z.literal('PERSONAL'),
    countryCode: z.string().optional(),
});

const AddInvoiceInformationCompanySchema = z.object({
    accountType: z.literal('COMPANY'),
    companyName: addInvoiceInformationString(),
    countryCode: addInvoiceInformationString(),
    vatNumber: addInvoiceInformationStringOptional(),
    street: addInvoiceInformationString(),
    city: addInvoiceInformationString(),
    postalCode: addInvoiceInformationString(),
});

export const AddInvoiceInformationSchema = z.discriminatedUnion('accountType', [
    AddInvoiceInformationPersonalSchema,
    AddInvoiceInformationCompanySchema,
]);

export const AddSubscriptionInstanceSchema = z.object({
    startDate: z.date(),
    endDate: z.date(),
    monthlyPrice: z.coerce.number(),
});

export const EditSubscriptionInstanceSchema = z.object({
    subscriptionTemplateId: z.string(),
    renewalStatus: z.enum(['PENDING', 'NEGOTIATION', 'APPROVED', 'REJECTED']),
    startDate: z.date(),
    endDate: z.date(),
    monthlyPrice: z.coerce.number(),
    invoiceNumber: z.string().optional(),
    invoiceDate: z.date().optional(),
    invoiceStatus: z
        .enum([InvoiceStatus.PENDING, InvoiceStatus.PAID, InvoiceStatus.OVERDUE, InvoiceStatus.CANCELLED])
        .optional(),
});

export const UpdateTeamSubscriptionPriceSchema = z.object({
    firstUserPrice: z.coerce.number(),
    additionalUserPrice: z.coerce.number(),
});

export const AddNewUserToTeamSchema = z.object({
    email: z.string().email(),
    firstName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    lastName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
});

// Legacy user
export const AddLegacyUserSchema = z.object({
    email: z.string().email(),
    firstName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    lastName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    teamRole: z.enum([TeamRole.OWNER, TeamRole.ADMIN, TeamRole.MEMBER]),
});

// New user
export const AddUserFromSalesSchema = z.object({
    email: z.string().email(),
    firstName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    lastName: z
        .string()
        .min(2, {
            message: 'Must be at least 2 characters.',
        })
        .max(50, {
            message: 'Must be at most 50 characters.',
        }),
    countryId: z.coerce.number().int().optional(),
    teamId: z.coerce.number().int().optional(),
});

export const SwitchTeamSchema = z.object({
    companyName: z.string(),
});

export const LineupStringSchema = z
    .object({
        sellerId: z.string().optional().nullable(),
        buyerId: z.string().optional().nullable(),
        broker: z.string().optional().nullable(),
        order: z.number().int(),
    })
    .superRefine(({ sellerId, buyerId, broker }, ctx) => {
        const allNull = [sellerId, buyerId, broker].every((v) => v == null);
        if (allNull) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'At least one of seller, buyer, or broker must be set.',
                path: ['buyerId'], // or [] if you want it as a form-level error
            });
        }
    });

const optionalNullableDate = (invalid_type_error: string) =>
    z
        .union([
            z.null(),
            z.coerce.date({
                invalid_type_error,
            }),
        ])
        .optional();

export const CreateLineupSchema = z
    .object({
        // todo delete me
        sellerId: z.string().nullable().optional(),
        // todo delete me
        intermediateClients: z.string().nullable().optional(),
        // todo delete me
        buyerId: z.string().nullable().optional(),
        // todo delete me
        broker: z.string().nullable().optional(),

        agentId: z.coerce
            .number()
            .transform((val) => (val === 0 ? undefined : val))
            .optional(),

        cropYearId: z
            .number({
                required_error: 'CropYear ID is required.',
                invalid_type_error: 'CropYear ID must be a number.',
            })
            .int()
            .positive()
            .optional(),

        eta: optionalNullableDate('ETA must be a valid date.'),

        blDate: optionalNullableDate('BL date must be a valid date.'),

        status: z.nativeEnum(LineupStatus).optional(),

        vesselName: z.string().optional(),
        vesselImo: z.number().optional(),
        vesselMarinetrafficId: z.number().optional(),

        productId: z.coerce
            .number({
                required_error: 'Product ID is required.',
                invalid_type_error: 'Product ID must be a number.',
            })
            .int()
            .positive(),

        proteinLevel: z.coerce.number().min(0).max(100).optional(),

        variantId: z.coerce
            .number()
            .transform((val) => (val === 0 ? undefined : val))
            .optional(),
        organic: z.boolean().optional(),

        volume: z.coerce
            .number({
                required_error: 'Volume is required.',
                invalid_type_error: 'Volume must be a number.',
            })
            .int()
            .positive(),

        originCountryIds: z.array(z.coerce.number().int().positive(), {
            required_error: 'At least one origin country is required.',
        }),
        // .min(1, 'At least one origin country must be provided.'),

        originRegionIds: z.array(z.coerce.number().int().positive()).optional(),

        loadPortId: z.coerce.number().int().positive(),
        loadCountryId: z.coerce.number().int().positive().optional(),
        loadBerth: z.string().optional(),
        strings: z.array(LineupStringSchema).optional(),
        dischargeCountryId: z.coerce.number().int().positive().optional(),
    })
    .superRefine((data, ctx) => {
        // At least ETA or BL date is required
        if (!data.eta && !data.blDate) {
            ctx.addIssue({
                path: ['eta'],
                code: 'custom',
                message: 'Either ETA or BL date must be provided.',
            });
            ctx.addIssue({
                path: ['blDate'],
                code: 'custom',
                message: 'Either BL date or ETA must be provided.',
            });
        }
    });

// Feature Flags
export const FeatureFlagSavePayloadSchema = z.object({
    flag_key: z.string().min(1, {
        message: 'Flag key is required.',
    }),
    default_value: z.boolean(),
    disabled: z.boolean(),
    team_targets: z
        .array(z.number().int())
        .nullable()
        .transform((val) => val ?? []),
    user_targets: z
        .array(z.string())
        .nullable()
        .transform((val) => val ?? []),
});

export const FeatureFlagsArraySchema = z.array(FeatureFlagSavePayloadSchema).min(1, {
    message: 'At least one feature flag must be provided.',
});
